import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import {
  createCategory,
  deleteCategory,
  getCategories,
  getCategory,
  udpateCategory,
  updatePosCategoryOrder,
  type CategoryIndexQueryKeyEntries,
  type CategoryShowQueryKeyEntries,
} from './categories.api'
import type {
  CategoryIdInput,
  CategoryIndexFilters,
  CreateCategoryInput,
  PosCategoryOrderInput,
  UpdateCategoryInput,
} from './categories.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface CategoryIndexScope extends OrganizationScope {
  filters?: CategoryIndexFilters | undefined
}

interface CategoryKeyScope extends OrganizationScope {
  categoryId?: CategoryIdInput | undefined
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type CategoryIndexQueryKey = [AdminQueryRoot & CategoryIndexQueryKeyEntries]
type CategoryShowQueryKey = [AdminQueryRoot & CategoryShowQueryKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

interface InvalidateOnSuccessOption {
  /** False for callers that update the category cache optimistically. */
  invalidateOnSuccess?: boolean
}

export const categoryQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'category' }, { organizationId }),
  ],
  index: ({ organizationId, filters }: CategoryIndexScope = {}): CategoryIndexQueryKey =>
    buildScopedAdminQueryKey(categoryQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      filters,
    }),
  show: ({ organizationId, categoryId }: CategoryKeyScope = {}): CategoryShowQueryKey =>
    buildScopedAdminQueryKey(categoryQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      categoryId,
    }),
}

export const categoryQueries = {
  index: ({ organizationId, filters }: CategoryIndexScope = {}) =>
    queryOptions({
      queryKey: categoryQueryKeys.index({ organizationId, filters }),
      queryFn: getCategories,
      staleTime: Infinity,
    }),
  show: ({ organizationId, categoryId }: CategoryKeyScope = {}) =>
    queryOptions({
      queryKey: categoryQueryKeys.show({ organizationId, categoryId }),
      queryFn: getCategory,
    }),
}

export function useCategories({
  organizationId,
  filters,
  params = {},
}: CategoryIndexScope & {
  params?: QueryParams<ReturnType<typeof categoryQueries.index>>
} = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...categoryQueries.index({ organizationId: resolvedOrganizationId, filters }),
    ...params,
  })
}

export function useCategory(
  categoryId: CategoryIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof categoryQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...categoryQueries.show({ organizationId: resolvedOrganizationId, categoryId }),
    ...params,
  })
}

export function useCreateCategoryMutation() {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (values: CreateCategoryInput) => createCategory({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: categoryQueryKeys.all({ organizationId }) }),
  })
}

export function useUpdateCategoryMutation({
  invalidateOnSuccess = true,
}: InvalidateOnSuccessOption = {}) {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (values: UpdateCategoryInput & { id: CategoryIdInput }) =>
      udpateCategory({ organizationId, ...values }),
    onSuccess: () => {
      if (!invalidateOnSuccess) {
        return
      }

      return queryClient.invalidateQueries({ queryKey: categoryQueryKeys.all({ organizationId }) })
    },
  })
}

export function useUpdatePosCategoryOrderMutation({
  invalidateOnSuccess = true,
}: InvalidateOnSuccessOption = {}) {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (values: { categories: PosCategoryOrderInput[] }) =>
      updatePosCategoryOrder({ organizationId, ...values }),
    onSuccess: () => {
      if (!invalidateOnSuccess) {
        return
      }

      return queryClient.invalidateQueries({ queryKey: categoryQueryKeys.all({ organizationId }) })
    },
  })
}

export function useDeleteCategoryMutation() {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (categoryId: CategoryIdInput) => deleteCategory({ organizationId, categoryId }),
    onSuccess: (_, categoryId) => {
      queryClient.removeQueries({
        queryKey: categoryQueryKeys.show({ organizationId, categoryId }),
      })
      return queryClient.invalidateQueries({ queryKey: categoryQueryKeys.all({ organizationId }) })
    },
  })
}
